import { useState } from "react";
import { Link } from "react-router-dom";

import { BarList } from "../components/BarList";
import { PairBars } from "../components/PairBars";
import { Card, ErrorBox, Loading } from "../components/Page";
import { StatTile } from "../components/StatTile";
import { api } from "../lib/api";
import { day, money, num, pct, usd } from "../lib/format";
import { useApi } from "../lib/useApi";

interface Group { key: string; label: string; ytd_usd: number; last_usd: number; change_usd: number; change: number | null }
interface Quarter { quarter: number; months: [number, number]; complete: boolean; usd: number; last_usd: number; change: number | null;
  cases: number; last_cases: number; cases_change: number | null }
interface YearRow { year: number; usd: number; cases: number; international_usd: number; us_usd: number; months: number; change: number | null }
interface Versus { key: string; label: string; measure: "sales" | "cases"; workbook: number; report: number; difference: number; agrees: boolean; workbook_as_of: string | null; report_as_of: string | null }
export interface Years {
  available: boolean; year: number; last_year: number; as_of: string | null; file: string;
  months_reported: number; months_elapsed: number; partial_month: boolean;
  ytd: { usd: number; last_usd: number; change: number | null; cases: number; last_cases: number; cases_change: number | null;
    price_per_case: number | null; last_price_per_case: number | null };
  pace_usd: number | null; last_year_total_usd: number;
  ttm: { usd: number; previous_usd: number | null; change: number | null; from: string; to: string };
  valuation: { multiple: number; usd: number } | null;
  goal: { amount_usd: number; remaining_usd: number; share_done: number | null; months_left: number;
    needed_per_month: number | null; average_per_month: number | null; last_year_rest_usd: number } | null;
  monthly: { month: number; usd: number | null; last_usd: number; cases: number | null; last_cases: number }[];
  groups: Group[]; quarters: Quarter[]; history: YearRow[]; versus_report: Versus[];
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "+6.3%" / "−6.0%" with an arrow, so the sign never rests on colour alone. */
function Change({ v }: { v: number | null | undefined }) {
  if (v == null) return <span className="muted">—</span>;
  const up = v >= 0;
  return <span className={up ? "up" : "down"}>{up ? "▲" : "▼"} {up ? "+" : "−"}{pct(Math.abs(v))}</span>;
}

const signed = (v: number | null | undefined) => (v == null ? "—" : `${v >= 0 ? "+" : "−"}${usd(Math.abs(v))}`);

export function SalesYears() {
  const { data, error, reload, setData } = useApi<Years>("/sales/years");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  if (!data.available) {
    return (
      <Card title="Year over year">
        <p className="note">No gross sales workbook yet. Upload Zach's workbook (one tab per year) on the{" "}
          <Link to="/sources/GROSS_SALES">Integrations</Link> page and every year since 2018 shows here.</p>
      </Card>
    );
  }
  const d = data;
  const span = `Jan–${MONTHS[d.months_reported - 1]}`;
  const disagree = d.versus_report.filter((v) => !v.agrees);
  return (
    <>
      <p className="note muted" data-testid="years-source">
        From Zach's gross sales workbook ({d.file}), as of {day(d.as_of)}. {d.year} is {span}, compared with the same
        months of {d.last_year}
        {d.partial_month ? <>; {MONTHS[d.months_reported - 1]} {d.year} is not over yet and is compared with all of {MONTHS[d.months_reported - 1]} {d.last_year}</> : null}.
      </p>
      <section className="tiles" aria-label="Year to date">
        <StatTile label={`Gross sales ${span}`} value={usd(d.ytd.usd)}
                  sub={`${d.ytd.change == null ? "" : `${d.ytd.change >= 0 ? "+" : "−"}${pct(Math.abs(d.ytd.change))} vs `}${usd(d.ytd.last_usd, true)} in ${d.last_year}`}
/>
        <StatTile label="Cases (6 × 750ml)" value={num(d.ytd.cases)}
                  sub={`${d.ytd.cases_change == null ? "" : `${d.ytd.cases_change >= 0 ? "+" : "−"}${pct(Math.abs(d.ytd.cases_change))} vs `}${num(d.ytd.last_cases)} in ${d.last_year}`} />
        <StatTile label="Average per case" value={money(d.ytd.price_per_case)} sub={`${money(d.ytd.last_price_per_case)} in ${d.last_year}`} />
        <StatTile label="Annualised pace" value={usd(d.pace_usd)}
                  sub={`over ${num(d.months_elapsed, 1)} months · ${d.last_year} full year ${usd(d.last_year_total_usd, true)}`} />
        <StatTile label="Last 12 months" value={usd(d.ttm.usd)}
                  sub={d.ttm.change == null ? `${day(d.ttm.from)} to ${MONTHS[d.months_reported - 1]} ${d.year}`
                    : `${d.ttm.change >= 0 ? "+" : "−"}${pct(Math.abs(d.ttm.change))} on the 12 before (${usd(d.ttm.previous_usd, true)})`} />
        {d.valuation ? (
          <StatTile label="Brand valuation" value={usd(d.valuation.usd, true)}
                    sub={`${num(d.valuation.multiple, 1)} × the annualised pace, as your sheet values it`} />
        ) : null}
      </section>

      <Goal d={d} onSaved={setData} />

      <Card title={`Gross sales by month, ${d.year} against ${d.last_year}`}>
        <PairBars title="Month" current={String(d.year)} previous={String(d.last_year)}
                  points={d.monthly.map((m) => ({ key: String(m.month), label: MONTHS[m.month - 1], value: m.usd, previous: m.last_usd,
                    valueLabel: m.usd == null ? "not yet" : usd(m.usd), previousLabel: usd(m.last_usd) }))} />
      </Card>

      <div className="two-up">
        <Card title={`By channel, ${span}`}>
          <div className="table-scroll">
            <table className="grid" data-testid="years-groups">
              <thead><tr><th>Channel</th><th className="n">{d.year}</th><th className="n">{d.last_year}</th><th className="n">Change</th><th className="n">%</th></tr></thead>
              <tbody>
                {d.groups.map((g) => (
                  <tr key={g.key}><td>{g.label}</td><td className="n">{usd(g.ytd_usd)}</td><td className="n">{usd(g.last_usd)}</td>
                    <td className="n">{signed(g.change_usd)}</td><td className="n"><Change v={g.change} /></td></tr>
                ))}
                <tr className="totals"><td>Total</td><td className="n">{usd(d.ytd.usd)}</td><td className="n">{usd(d.ytd.last_usd)}</td>
                  <td className="n">{signed(d.ytd.usd - d.ytd.last_usd)}</td><td className="n"><Change v={d.ytd.change} /></td></tr>
              </tbody>
            </table>
          </div>
          <p className="note muted small">Caribbean is Dufry and the direct island customers; River Valley is Alberta and
            Australia; USA was OPICI until 2024 and MHW since.</p>
        </Card>
        <Card title="By quarter">
          <div className="table-scroll">
            <table className="grid" data-testid="years-quarters">
              <thead><tr><th>Quarter</th><th className="n">{d.year}</th><th className="n">{d.last_year}</th><th className="n">%</th><th className="n">Cases</th><th className="n">%</th></tr></thead>
              <tbody>
                {d.quarters.map((q) => (
                  <tr key={q.quarter}>
                    <td>Q{q.quarter}{q.complete ? null : <div className="muted small">{MONTHS[q.months[0] - 1]}{q.months[1] !== q.months[0] ? `–${MONTHS[q.months[1] - 1]}` : ""} so far</div>}</td>
                    <td className="n">{usd(q.usd)}</td><td className="n">{usd(q.last_usd)}</td><td className="n"><Change v={q.change} /></td>
                    <td className="n">{num(q.cases)}</td><td className="n"><Change v={q.cases_change} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div className="two-up">
        <Card title="Every year">
          <BarList bars={d.history.slice().reverse().map((h) => ({
            key: String(h.year), label: h.months < 12 ? `${h.year} (${h.months} months)` : String(h.year), value: h.usd,
            valueLabel: usd(h.usd), meta: h.change == null ? undefined : `${h.change >= 0 ? "+" : "−"}${pct(Math.abs(h.change))}` }))} />
          <details className="small">
            <summary>International, US and cases, by year</summary>
            <div className="table-scroll">
              <table className="grid compact" data-testid="years-history">
                <thead><tr><th>Year</th><th className="n">International</th><th className="n">US</th><th className="n">Total</th><th className="n">Cases</th></tr></thead>
                <tbody>{d.history.map((h) => (
                  <tr key={h.year}><td>{h.year}</td><td className="n">{usd(h.international_usd)}</td><td className="n">{usd(h.us_usd)}</td>
                    <td className="n">{usd(h.usd)}</td><td className="n">{num(h.cases, 1)}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </details>
        </Card>
        <Card title="Two of Zach's sheets compared">
          <p className="note">
            {d.year} in this workbook against the sales report, which lists every invoice.{" "}
            {d.versus_report[0] && d.versus_report[0].workbook_as_of !== d.versus_report[0].report_as_of
              ? <>The workbook is from {day(d.versus_report[0].workbook_as_of)} and the sales report from {day(d.versus_report[0].report_as_of)},
                  so invoices added in between show as a difference.{" "}</> : null}
            {disagree.length ? `${disagree.length} of ${d.versus_report.length} figures differ.` : "They agree."}
          </p>
          {d.versus_report.length ? (
            <div className="table-scroll">
              <table className="grid compact" data-testid="years-versus">
                <thead><tr><th /><th className="n">Workbook</th><th className="n">Sales report</th><th className="n">Difference</th></tr></thead>
                <tbody>{d.versus_report.map((v) => {
                  const fmt = v.measure === "sales" ? (x: number) => money(x) : (x: number) => num(x);
                  return (
                    <tr key={v.key + v.measure}>
                      <td>{v.label}</td><td className="n">{fmt(v.workbook)}</td><td className="n">{fmt(v.report)}</td>
                      <td className="n">{v.agrees ? <span className="muted">same</span> : `${v.difference >= 0 ? "+" : "−"}${fmt(Math.abs(v.difference))}`}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          ) : null}
          {disagree.length ? <p className="note"><Link to="/data-quality">See them on Data quality</Link></p> : null}
        </Card>
      </div>
    </>
  );
}

/** The year's sales target, typed by Zach; everything against it is worked out. */
function Goal({ d, onSaved }: { d: Years; onSaved: (y: Years) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(d.goal ? String(d.goal.amount_usd) : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const g = d.goal;

  async function save(amount: number | null) {
    setBusy(true); setErr(null);
    try {
      onSaved(await api.put<Years>("/sales/goal", { year: d.year, amount_usd: amount }));
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const form = (
    <form className="goal-form" onSubmit={(e) => { e.preventDefault(); const n = Number(value.replace(/[$,\s]/g, "")); if (n > 0) void save(n); }}>
      <label className="field"><span className="field-label">Goal for {d.year} (USD)
        </span><input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder="2,500,000" aria-label={`Sales goal for ${d.year}`} />
      </label>
      <button className="btn btn-primary" type="submit" disabled={busy}>Save goal</button>
      {g ? <button className="btn" type="button" disabled={busy} onClick={() => void save(null)}>Remove goal</button> : null}
      {g ? <button className="btn" type="button" onClick={() => setEditing(false)}>Cancel</button> : null}
      {err ? <span className="down" role="alert">{err}</span> : null}
    </form>
  );

  if (!g) {
    return (
      <Card title={`Goal for ${d.year}`}>
        <p className="note">No goal set. Type the year's target and the page works out what is left and what each remaining month needs.</p>
        {form}
      </Card>
    );
  }
  const done = Math.min(g.share_done ?? 0, 1);
  return (
    <Card title={`Goal for ${d.year}: ${usd(g.amount_usd)}`} actions={editing ? null : <button className="link-btn" onClick={() => setEditing(true)}>Change</button>}>
      <div data-testid="sales-goal">
        <div className="goal-track" role="img" aria-label={`${pct(g.share_done)} of the goal`}>
          <span className="goal-fill" style={{ inlineSize: `${Math.max(done * 100, 0.6)}%` }} />
        </div>
        <p className="note">
          <strong>{usd(d.ytd.usd)}</strong> sold, {pct(g.share_done)} of the goal.{" "}
          {g.remaining_usd > 0 ? (
            <>
              <strong>{usd(g.remaining_usd)}</strong> to go in about {num(g.months_left, 1)} months:{" "}
              <strong>{usd(g.needed_per_month)} a month</strong>, against {usd(g.average_per_month)} a month so far this year.
              {d.months_reported < 12 ? <>{" "}{MONTHS[d.months_reported]}{d.months_reported < 11 ? "–Dec" : ""} {d.last_year} brought in {usd(g.last_year_rest_usd)}.</> : null}
            </>
          ) : <>The goal is reached.</>}
        </p>
      </div>
      {editing ? form : null}
    </Card>
  );
}
